import React from 'react';
import styles from './Grid.module.css';
import { SpaceToken, getSpaceValue } from '../../utils/spaceToken/spaceToken';
import { responsiveToCssVars } from '../../utils/breakpoint/responsiveToCssVars';
import { WithResponsiveProps } from '../../utils/breakpoint/responsiveSSR';
import { ResponsiveProp } from '../../utils/breakpoint/responsive';

type AutoFlowValue = 'row' | 'column' | 'dense' | 'row dense' | 'column dense';
type AlignContentValue =
  | 'start'
  | 'end'
  | 'center'
  | 'stretch'
  | 'space-around'
  | 'space-between'
  | 'space-evenly';

type JustifyContentValue =
  | 'start'
  | 'end'
  | 'center'
  | 'stretch'
  | 'space-around'
  | 'space-between'
  | 'space-evenly';

// Define valid HTML elements for polymorphic prop
type ValidElements = 'div' | 'span';

// Helper type for polymorphic components
export type PolymorphicProps<E extends ValidElements> = {
  as?: E;
} & Omit<React.ComponentPropsWithRef<E>, 'as'>;

// GridCell Types and Component
export interface GridCellBaseProps {
  /**
   * **[Responsive]** Number of columns/rows this cell should span
   * Can be a single number for column span or an object for both
   */
  span?:
    | ResponsiveProp<number>
    | { column?: ResponsiveProp<number>; row?: ResponsiveProp<number> };
  /**
   * Named grid area for placing the cell
   * Takes precedence over `column` / `row` props if both are set
   */
  area?: string;
  /**
   * **[Responsive]** Short-hand property to specify a cell's size and location within the Grid's columns.
   * Accepts any valid value for CSS `grid-column`. Takes precedence over `span` prop if both are set
   */
  column?: ResponsiveProp<string | number>;
  /**
   * Starting column for the cell
   */
  columnStart?: number;
  /**
   * Ending column for spanning multiple columns
   */
  columnEnd?: number;
  /**
   * **[Responsive]** Short-hand property to specify a cell's size and location within the Grid's rows.
   * Accepts any valid value for CSS `grid-row`. Takes precedence over `span` prop if both are set
   */
  row?: ResponsiveProp<string | number>;
  /**
   * Starting row for the cell
   */
  rowStart?: number;
  /**
   * Ending row for spanning multiple rows
   */
  rowEnd?: number;
  /**
   * The content of the grid cell
   */
  children: React.ReactNode;
  /**
   * @internal
   * Additional CSS properties to apply to the grid cell
   */
  style?: React.CSSProperties;
}

export type GridCellProps<E extends ValidElements = 'div'> = GridCellBaseProps &
  PolymorphicProps<E>;

export const GridCell = <E extends ValidElements = 'div'>({
  as,
  span,
  area,
  column,
  columnStart,
  columnEnd,
  row,
  rowStart,
  rowEnd,
  children,
  style,
  ...rest
}: GridCellProps<E>) => {
  // Determine the HTML element to render
  const Component = (as || 'div') as React.ElementType;

  // Handle the span prop logic
  const getSpanVars = () => {
    if (!span) return {};

    if (
      typeof span === 'number' ||
      (typeof span === 'object' && 'base' in span)
    ) {
      return responsiveToCssVars(span as number, {
        varPrefix: '--grid-cell-grid-column',
        normalise: (value) => `span ${value}`,
      });
    }

    const columnVars =
      'column' in span
        ? responsiveToCssVars(span.column, {
            varPrefix: '--grid-cell-grid-column',
            normalise: (value) => `span ${value}`,
          })
        : {};

    const rowVars =
      'row' in span
        ? responsiveToCssVars(span.row, {
            varPrefix: '--grid-cell-grid-row',
            normalise: (value) => `span ${value}`,
          })
        : {};

    return { ...columnVars, ...rowVars };
  };

  const gridCellStyle = {
    // Grid area takes precedence over individual positioning
    ...(area && { gridArea: area }),

    // Apply span styles
    ...getSpanVars(),

    // If no area is specified, use individual positioning props
    ...(!area && {
      ...(column &&
        responsiveToCssVars(column, {
          varPrefix: '--grid-cell-grid-column',
          normalise: (value) => `${value}`,
        })),
      ...(columnStart && { gridColumnStart: columnStart }),
      ...(columnEnd && { gridColumnEnd: columnEnd }),
      ...(row &&
        responsiveToCssVars(row, {
          varPrefix: '--grid-cell-grid-row',
          normalise: (value) => `${value}`,
        })),
      ...(rowStart && { gridRowStart: rowStart }),
      ...(rowEnd && { gridRowEnd: rowEnd }),
    }),

    // Apply any custom styles
    ...style,
  };

  return (
    <Component className={styles['grid-cell']} style={gridCellStyle} {...rest}>
      {children}
    </Component>
  );
};

// Grid Types and Component
export interface GridBaseProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Optional unique identifier for the grid
   */
  id?: string;
  /**
   * Sets the display to inline-grid if true, and grid if false
   * @default false
   */
  isInline?: boolean;
  /**
   * **[Responsive]** Controls the vertical alignment of the content within the grid
   */
  alignContent?: AlignContentValue;
  /**
   * **[Responsive]** Controls the horizontal alignment of the content within the grid
   */
  justifyContent?: JustifyContentValue;
  /**
   * Determines how columns are automatically created in the grid
   * e.g., "minmax(100px, 1fr)" or "200px"
   */
  autoColumns?: string;
  /**
   * Sets the flow direction and behavior of the grid's auto-placement
   */
  autoFlow?: AutoFlowValue;
  /**
   * **[Responsive]** Number of columns to create (generates equal-width columns)
   * Takes precedence over templateColumns if both are provided
   */
  columns?: number;
  /**
   * **[Responsive]** Number of rows to create (generates equal-height rows)
   * Takes precedence over templateRows if both are provided
   */
  rows?: number;
  /**
   * **[Responsive]** Defines the columns of the grid using CSS grid-template-columns syntax
   * e.g. "1fr 1fr 1fr" or "200px 1fr 200px"
   */
  templateColumns?: string;
  /**
   * **[Responsive]**  Defines the rows of the grid using CSS grid-template-rows syntax
   * e.g. "auto auto" or "100px 1fr"
   */
  templateRows?: string;
  /**
   * **[Responsive]** Controls the overall space between grid cells
   * @default 'default'
   */
  gap?: SpaceToken;
  /**
   * **[Responsive]** Controls the vertical space between rows
   * @default 'default'
   */
  rowGap?: SpaceToken;
  /**
   * **[Responsive]** Controls the horizontal space between columns
   * @default 'default'
   */
  columnGap?: SpaceToken;
  /**
   * The content of the grid
   */
  children?: React.ReactNode;
  /**
   * @internal
   * Additional CSS properties to apply to the grid
   */
  style?: React.CSSProperties;
}

type ResponsivePropKeys =
  | 'columns'
  | 'rows'
  | 'gap'
  | 'rowGap'
  | 'columnGap'
  | 'alignContent'
  | 'templateColumns'
  | 'templateRows';

export type GridProps = WithResponsiveProps<GridBaseProps, ResponsivePropKeys>;

export const Grid = ({
  id,
  isInline = false,
  alignContent,
  justifyContent,
  autoColumns,
  autoFlow,
  columns,
  rows,
  templateColumns,
  templateRows,
  gap,
  rowGap,
  columnGap,
  children,
  style,
  ...rest
}: GridProps) => {
  /**
   * Responsive Props
   */
  // columns take precedence over templateColumns
  const templateColumnsVars =
    columns != null
      ? responsiveToCssVars(columns, {
          varPrefix: '--grid-template-columns',
          normalise: (val) => `repeat(${val}, 1fr)`,
        })
      : templateColumns
        ? responsiveToCssVars(templateColumns, {
            varPrefix: '--grid-template-columns',
            normalise: (val) => val,
          })
        : {};

  const templateRowsVars =
    rows != null
      ? responsiveToCssVars(rows, {
          varPrefix: '--grid-template-rows',
          normalise: (val) => `repeat(${val}, 1fr)`,
        })
      : templateRows
        ? responsiveToCssVars(templateRows, {
            varPrefix: '--grid-template-rows',
            normalise: (val) => val,
          })
        : {};

  // Preserve "default" behaviour: if no gap/rowGap/columnGap, use 'default' at base
  const resolvedGap: typeof gap =
    gap ?? (rowGap == null && columnGap == null ? 'default' : undefined);

  const gapVars = responsiveToCssVars(resolvedGap ?? null, {
    varPrefix: '--grid-gap',
    normalise: (token) => getSpaceValue(token as SpaceToken),
  });

  const rowGapVars = responsiveToCssVars(rowGap ?? null, {
    varPrefix: '--grid-row-gap',
    normalise: (token) => getSpaceValue(token as SpaceToken),
  });

  const columnGapVars = responsiveToCssVars(columnGap ?? null, {
    varPrefix: '--grid-column-gap',
    normalise: (token) => getSpaceValue(token as SpaceToken),
  });

  // Responsive alignContent via CSS vars

  const alignContentVars = responsiveToCssVars(alignContent ?? null, {
    varPrefix: '--grid-align-content',
    normalise: (val) => val,
  });

  const gridStyle: React.CSSProperties = {
    display: isInline ? 'inline-grid' : 'grid',
    gridAutoColumns: autoColumns,
    gridAutoFlow: autoFlow,
    justifyContent: justifyContent,
    ...templateColumnsVars,
    ...templateRowsVars,
    ...gapVars,
    ...rowGapVars,
    ...columnGapVars,
    ...alignContentVars,
    ...style,
  };

  return (
    <div id={id} className={styles.grid} style={gridStyle} {...rest}>
      {children}
    </div>
  );
};

const isValidElement = (value: unknown): value is ValidElements => {
  const validElements: ValidElements[] = ['div', 'span'];
  return (
    typeof value === 'string' && validElements.includes(value as ValidElements)
  );
};

Grid.Cell = GridCell;

export { isValidElement };
export default Grid;
